<template>
  <div class="supplier-contact-form">
    <form class="vertical">
      <section v-if="canViewUserAccount">
        <h1>{{ $t('supplierContacts.labels.userAccount') }}</h1>
        <div>
          <router-link :to="`/users/${formData.user.reference_number}`"
            ><font-awesome-icon icon="user"></font-awesome-icon>
            {{ formData.user.username }}</router-link
          >
          <span v-if="!formData.user.is_active" class="inactive">
            {{ $t('users.labels.inactiveUser') }}
          </span>
        </div>
      </section>

      <section>
        <div class="supplier-contacts-form-container">
          <div class="form-control">
            <label>{{ $t('supplierContacts.labels.lastName') }}</label>
            <InputField
              v-model="formData.user.last_name"
              type="text"
              class="bordered"
              @change="onChange"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('supplierContacts.labels.firstName') }}</label>
            <InputField
              v-model="formData.user.first_name"
              type="text"
              class="bordered"
              @change="onChange"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('supplierContacts.labels.preferredName') }}</label>
            <InputField
              v-model="formData.user.preferred_name"
              type="text"
              class="bordered"
              @change="onChange"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('supplierContacts.labels.supplier') }}</label>
            {{ formData.suppliers.supplier_name }}
            <TolTypeAhead
              v-show="!formData.suppliers.name"
              :search-params="{
                url: '/suppliers/supplier-quick-search',
                searchKeys: ['name']
              }"
              class="blue contact-box bordered"
              :menu-label="'Supplier Name'"
              @select-item="onSupplierSelect($event)"
            >
              <template v-slot="{ value }">
                {{ value.name }}
              </template>
            </TolTypeAhead>
            <tol-list :list-items="formData.suppliers">
              <template v-slot:body="{ value }">
                <div class="rate-list-body">
                  <span>{{ value.name }}</span>
                  <span class="list-action" @click="removeOnScreenValue(value.id)">
                    <font-awesome-icon icon="minus-circle"></font-awesome-icon>
                  </span>
                </div>
              </template>
            </tol-list>
          </div>
          <div class="break" />

          <div class="form-control">
            <label>{{ $t('supplierContacts.labels.emails') }}</label>
            <InputField
              v-model="formData.user.email"
              type="text"
              class="bordered"
              field-icon="envelope"
              @change="onChange"
            />
            <InputField
              v-model="formData.user.email_alt"
              type="text"
              class="bordered"
              field-icon="envelope"
              @change="onChange"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('supplierContacts.labels.phoneNumbers') }}</label>
            <div class="multi-inline">
              <InputField
                v-model="formData.user.phone1"
                type="text"
                class="bordered"
                field-icon="phone-square"
                @change="onChange"
              />
              <DropdownSelect
                :menu-items="phoneTypeOptions"
                :selected-item="camelCaseOptions(formData.user.phone1_type)"
                class="blue bordered"
                @select-item="onPhone1TypeChange"
              />
            </div>
            <div class="multi-inline">
              <InputField
                v-model="formData.user.phone2"
                type="text"
                class="bordered"
                field-icon="phone-square"
                @change="onChange"
              />
              <DropdownSelect
                :menu-items="phoneTypeOptions"
                :selected-item="camelCaseOptions(formData.user.phone2_type)"
                class="blue bordered"
                @select-item="onPhone2TypeChange"
              />
            </div>
            <div class="multi-inline">
              <InputField
                v-model="formData.user.phone3"
                type="text"
                class="bordered"
                field-icon="phone-square"
                @change="onChange"
              />
              <DropdownSelect
                :menu-items="phoneTypeOptions"
                :selected-item="camelCaseOptions(formData.user.phone3_type)"
                class="blue bordered"
                @select-item="onPhone1TypeChange"
              />
            </div>
          </div>
        </div>
        <div class="break" />

        <div class="form-control">
          <label>{{ $t('supplierContacts.labels.active') }}</label>
          <checkbox
            size="tiny"
            :checked="formData.user.is_active"
            @change="updateProfileStatus()"
          />
        </div>
        <div class="supplier-contact-action-container">
          <tol-button
            id="save-btn"
            :button-type="'minor'"
            icon-name="save"
            :class="['supplier-contact-save', { disabled: !isOnScreenDataModified }]"
            tooltip="SAVE"
            @click="doSupplierContactSave()"
          >
          </tol-button>
        </div>
      </section>
    </form>
  </div>
</template>
<script>
import { AppGetters } from '@/app/app.keys';
import { SupplierContactActions } from '../supplier-contact-details.keys';
import InputField from '@/app/components/InputField';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import TolList from '@/app/components/TolList';
import DropdownSelect from '@/app/components/DropdownSelect';
import TolButton from '@/app/components/TolButton';
import Checkbox from '@/app/components/Checkbox';
import * as R from 'ramda';
import camelCase from '@/filters/camel-case.filter';
export default {
  components: {
    InputField,
    TolTypeAhead,
    TolList,
    DropdownSelect,
    TolButton,
    Checkbox
  },
  props: {
    supplierContact: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      formData: {},
      isOnScreenDataModified: false
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    canViewUserAccount() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['USER_ACCOUNT'] &&
        !!this.currentUser.permissions['USER_ACCOUNT']['VIEW']
      );
    },
    getSupplier() {
      return this.clientContact.clients.map(c => c.name);
    },
    phoneTypeOptions() {
      let phoneTypes = this.$store.getters[AppGetters.PhoneTypes].map(
        type => type[0].toUpperCase() + type.substring(1).toLowerCase()
      );
      return phoneTypes;
    }
  },
  watch: {
    supplierContact: {
      immediate: true,
      handler() {
        this.formData = R.clone(this.supplierContact);
      }
    }
  },
  methods: {
    camelCaseOptions(string) {
      if (string) {
        return camelCase(string);
      }
      return string;
    },
    onSupplierSelect(event) {
      this.isOnScreenDataModified = true;
      this.formData.suppliers.push({
        id: event.id,
        name: event.name,
        reference_number: event.reference_number
      });
    },
    removeOnScreenValue(id) {
      this.isOnScreenDataModified = true;
      this.formData.suppliers = this.formData.suppliers.filter(element => element.id !== id);
    },
    onChange() {
      this.isOnScreenDataModified = true;
    },
    updateProfileStatus() {
      this.isOnScreenDataModified = true;
      let formDataCopy = { ...this.formData };
      formDataCopy.user.is_active = !formDataCopy.user.is_active;
      this.formData = formDataCopy;
    },
    onPhone1TypeChange(event) {
      this.formData.user.phone1_type = event.toUpperCase();
      this.isOnScreenDataModified = true;
    },
    onPhone2TypeChange(event) {
      this.formData.user.phone2_type = event.toUpperCase();
      this.isOnScreenDataModified = true;
    },
    onPhone3TypeChange(event) {
      this.formData.user.phone3_type = event.toUpperCase();
      this.isOnScreenDataModified = true;
    },
    doSupplierContactSave() {
      this.isProcessBtnClicked = true;
      this.$store.dispatch(SupplierContactActions.SaveSupplierContact, this.formData);
      window.setTimeout(() => {
        this.isProcessBtnClicked = false;
        this.isOnScreenDataModified = false;
      }, 2000);
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@/app/styles/colors.scss';

.supplier-contacts-form-container {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  width: 70%;

  & div {
    width: 20vw;
    margin: 5px 0 5px 0;
  }
}

.supplier-contact-form {
  form {
    width: 100%;
    section {
      margin-bottom: 20px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      background-color: $background-level-3;

      h1 {
        padding-top: 15px;
        border-bottom: 1px solid $row-border;
      }
      h1,
      h2 {
        flex: 0 100%;
      }

      a {
        text-decoration: none;
      }

      .break {
        width: 100%;
        height: 0;
      }

      .inactive {
        color: $red;
        border: 1px solid $red;
        padding: 0 5px;
        margin-left: 5px;
      }

      .form-control {
        padding: 10px 0;
        margin: 1em;
        flex: 1;
        > label {
          display: block;
          font-weight: bold;
        }

        .multi-inline {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;

          > * {
            margin-left: 1em;
          }
          > *:first-child {
            margin-left: 0;
          }
          .dropdown-container {
            width: 30%;
          }
        }
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }
    }
    .bordered {
      border: 1px solid $blue;
      font-size: 1em;
      &:focus {
        outline: none;
      }
    }

    .supplier-contact-action-container {
      display: flex;
      position: relative;
      justify-content: space-between;
      background-color: $background-level-3;
      padding: 60px 20px;
      width: 100%;
      margin: 30px 0;
      .supplier-contact-save {
        width: 50px;
        height: 50px;
        padding: 0;
        font-size: 1.5em;
        margin-right: 20px;
        color: $white;
        border-radius: 100%;
        background-color: $secondary-green;
        &.disabled {
          background-color: $light-grey-2;
          color: $light-grey-1;
          &:hover {
            background-color: $light-grey-2;
            color: $light-grey-1;
            cursor: not-allowed;
          }
        }
      }
    }
  }
}
</style>
